import { useEffect, useState } from "react";
import DefaultFormParserLayout from "../../../components/Layouts/default-form-parser-layout";
import Button from "../../../components/UI/Button";
import Input from "../../../components/UI/Input";
import RangePicker from "../../../components/UI/RangePicker";
import SmartSearch from "../../../components/UI/SmartSearch";
import { IParams } from "../../../context/ListRecordProvider/types";
import { useLoading } from "../../../context/LoadingProvider/useLoading";
import {
  GetHistoricalTransactionLogParams,
  getHistoricalTransactionLog,
} from "../../../services/reports";
import { Output } from "../../../services/types";
import { Space } from "antd";
import { DateFormatEnum } from "../../../enums/dateFormatEnum";
import { notificationError } from "../../../services/notification";
import { getUsersList } from "../../../services/users";
import { useAuth } from "../../../context/AuthProvider/useAuth";
import dayjs from "dayjs";
import {convertToCentralSameTime, getCentralCurrentTime} from "../../../utils/dateUtils";

type Form = {
  event: string;
  device: string;
  form: string;
  serial: string;
  limit: string;
};

function HistoricalTransactionLog() {
  const  affiliation  = useAuth().response?.affiliation

  const centralCurrentTime = getCentralCurrentTime()
  const initDateStart = centralCurrentTime.format('YYYY-MM-DDTHH:mm:ss')
  const LAST_HOUR = dayjs(initDateStart).subtract(1, "hour");
  const TODAY = dayjs(initDateStart);

  const loading = useLoading();

  const [params, setParams] = useState<IParams>({
    id: null,
    name: "",
    type: "",
  });

  const [search, setSearch] = useState("");

  const [form, setForm] = useState<Form>({
    event: "",
    device: "",
    form: "",
    serial: "",
    limit: "500",
  });

  const [validSearch, setValidSearch] = useState(false);
  const [report, setReport] = useState("");
  const [timeInterval, setTimeInterval] = useState([LAST_HOUR, TODAY]);

  const handleEnter = (event: KeyboardEvent) => {
    if (loading.loading) return;
    if (!event.repeat && event.key === 'Enter') {
      refreshReport()
    }
  }

  useEffect(() => {
    document.addEventListener("keydown", handleEnter)
    return () => document.removeEventListener("keydown", handleEnter)
  })

  const refreshReport = async (output: Output = Output.HTML) => {
    loading.addLoading();

    const dateStart = dayjs(timeInterval[0].valueOf()).format('YYYY-MM-DDTHH:mm:ss')
    const dateEnd = dayjs(timeInterval[1].valueOf()).format('YYYY-MM-DDTHH:mm:ss')
    let dayjsStart = convertToCentralSameTime(dateStart)
    let dayjsEnd = convertToCentralSameTime(dateEnd)

    if(affiliation === 'manufacturer' || affiliation === 'investigator') {
      if (dayjs(dateEnd).diff(dayjs(dateStart), 'hours') > 24) {
        loading.removeLoading()
        notificationError('Time range cannot exceed 24 hours')
        return
      }
    }
    else {
      if (dayjs(dateEnd).diff(dayjs(dateStart), 'hours') > 6) {
        loading.removeLoading()
        notificationError('Time range cannot exceed 6 hours')
        return
      }
    }
    if(parseInt(form.limit) > 10000) {
      loading.removeLoading()
      notificationError('Return limit cannot be greater than 10000')
      return
    }
    const getHistoricalTransactionLogParams: GetHistoricalTransactionLogParams =
      {
        output,
        dateIntervalStart: dayjsStart.valueOf(),
        dateIntervalEnd: dayjsEnd.valueOf(),
        [`${params.type}Id`]: params.id,
      };

    if (!!form.event) {
      getHistoricalTransactionLogParams.event = form.event;
    }

    if (!!form.device) {
      getHistoricalTransactionLogParams.device = form.device;
    }

    if (!!form.form) {
      getHistoricalTransactionLogParams.form = form.form;
    }

    if (!!form.serial) {
      getHistoricalTransactionLogParams.serial = form.serial;
    }

    if (!!form.limit) {
      getHistoricalTransactionLogParams.limit = form.limit;
    }

    const res = await getHistoricalTransactionLog(
      getHistoricalTransactionLogParams
    );

    if (Output.isHTML(output)) {
      setReport(res);
    }

    loading.removeLoading();
  };

  return (
    <DefaultFormParserLayout html={report}>
      <Space direction="horizontal" size="middle" wrap>
        <SmartSearch
          label="Smart Search"
          searchBy={["location"]}
          onChange={(e: string) => setSearch(e)}
          status="active,closed"
          value={search}
          onValidSearch={setValidSearch}
          onObjValueChange={(e: any) => setParams({ ...params, ...e })}
          autoFocus
        />

        <Input
          label="Event"
          onChange={(e: any) => setForm({ ...form, event: e.target.value })}
          value={form.event}
        />

        <Input
          label="Device"
          onChange={(e: any) => setForm({ ...form, device: e.target.value })}
          value={form.device}
        />

        <Input
          label="Form"
          onChange={(e: any) => setForm({ ...form, form: e.target.value })}
          value={form.form}
        />

        <Input
          label="Serial"
          onChange={(e: any) => setForm({ ...form, serial: e.target.value })}
          value={form.serial}
        />

        <Input
          label="Limit"
          onChange={(e: any) => setForm({ ...form, limit: e.target.value })}
          value={form.limit}
          type="number"
          inputProps={{
            min: 0,
          }}
        />
        <RangePicker
          label="Date Interval"
          defaultValue={[LAST_HOUR, TODAY]}
          onChange={(e: any) => setTimeInterval(e)}
          format={DateFormatEnum.MONTH_DAY_YAEAR_TIME}
          showTime
          allowClear={false}
        />
        <Button
          type="primary"
          disabled={!validSearch}
          onClick={() => refreshReport()}
        >
          Refresh
        </Button>

        <Button
          type="primary"
          disabled={!validSearch}
          onClick={() => refreshReport(Output.CSV)}
        >
          Save as CSV
        </Button>

        <Button
          type="primary"
          disabled={!validSearch}
          onClick={() => refreshReport(Output.PDF)}
        >
          Save as PDF
        </Button>
      </Space>

    </DefaultFormParserLayout>
  );
}

export default HistoricalTransactionLog;
